/**
 * A formula's picture (`check.picture`) read for drawing: the glyph each
 * aggregate is drawn as, the filter they share, and the picture as a line of
 * plain text for a breakdown part's caption ("Σ Shares × EPS").
 *
 * Drawn lighter than the engine's words (measured in a browser, 2026-10-18):
 * an aggregate is a glyph followed by its name chips, its label word only the
 * glyph's title; a filter every aggregate shares is said once, after the
 * picture, rather than beside each of them.
 */
import type { Picture } from '@/features/mitra/api/dashboard'

type Aggregate = Extract<Picture, { t: 'agg' }>

/** An aggregate as a glyph. An aggregate this table does not know keeps its label. */
const GLYPH: Record<string, string> = {
  sum: 'Σ',
  total: 'ΣΣ',
  avg: 'avg',
  wavg: 'w.avg',
  count: '#',
  lowest: 'min',
  highest: 'max',
}

export const glyphOf = (node: Aggregate): string => GLYPH[node.fn] ?? node.label

/** Every aggregate in the picture, left to right: the order the breakdown parts come in. */
export function aggregatesOf(node: Picture): Aggregate[] {
  switch (node.t) {
    case 'agg':
      return [
        node,
        ...(node.of ? aggregatesOf(node.of) : []),
        ...(node.by ? aggregatesOf(node.by) : []),
      ]
    case 'neg':
      return aggregatesOf(node.of)
    case 'frac':
    case 'pct':
      return [...aggregatesOf(node.num), ...aggregatesOf(node.den)]
    case 'op':
      return [...aggregatesOf(node.left), ...aggregatesOf(node.right)]
    case 'fn':
      return node.args.flatMap(aggregatesOf)
    default:
      return []
  }
}

/** The filter every aggregate shares, said once; null where they differ or there is none. */
export function sharedWhere(node: Picture): string | null {
  const wheres = aggregatesOf(node).map((a) => a.where ?? null)
  const first = wheres[0] ?? null
  return first !== null && wheres.every((w) => w === first) ? first : null
}

/** The picture as plain text: "Σ Value ÷ Σ Cost %". */
export function pictureText(node: Picture): string {
  switch (node.t) {
    case 'name':
      return node.label
    case 'num':
    case 'text':
      return node.value
    case 'neg':
      return `−${pictureText(node.of)}`
    case 'op':
      return `${pictureText(node.left)} ${node.op} ${pictureText(node.right)}`
    case 'frac':
      return `${pictureText(node.num)} ÷ ${pictureText(node.den)}`
    case 'pct':
      return `${pictureText(node.num)} ÷ ${pictureText(node.den)} %`
    case 'agg':
      return [
        glyphOf(node),
        ...(node.of ? [pictureText(node.of)] : []),
        ...(node.by ? ['by', pictureText(node.by)] : []),
      ].join(' ')
    case 'fn':
      return `${node.label}(${node.args.map(pictureText).join(', ')})`
  }
}

/** Every figure name in the picture with the engine's short label: "cash_div_per_share" -> "Cash div/share". */
export function labelsOf(node: Picture, into = new Map<string, string>()): Map<string, string> {
  switch (node.t) {
    case 'name':
      into.set(node.name, node.label)
      break
    case 'agg':
      if (node.of) labelsOf(node.of, into)
      if (node.by) labelsOf(node.by, into)
      break
    case 'neg':
      labelsOf(node.of, into)
      break
    case 'frac':
    case 'pct':
      labelsOf(node.num, into)
      labelsOf(node.den, into)
      break
    case 'op':
      labelsOf(node.left, into)
      labelsOf(node.right, into)
      break
    case 'fn':
      for (const arg of node.args) labelsOf(arg, into)
      break
    default:
      break
  }
  return into
}
